import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase/client';
import { toast } from 'sonner';
import { useSupabaseAuth } from '../auth/useSupabaseAuth';

export interface Service {
  id: string;
  service_name: string;
  price: number;
  is_offered: boolean;
  service_type?: 'home' | 'vehicle';
  created_at?: string;
  updated_at?: string;
}

export function useServices(businessId: number | null) {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);
  const { isSignedIn } = useSupabaseAuth();

  const loadServices = useCallback(async () => {
    if (!businessId || !isSignedIn) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Loading services for business:', businessId);
      
      const { data, error: fetchError } = await supabase
        .from('services')
        .select('*')
        .eq('user_id', businessId)
        .order('service_name');

      if (fetchError) throw fetchError;

      // Map the data to our Service interface
      const mappedServices: Service[] = data?.map(item => ({
        id: item.id,
        service_name: item.service_name,
        price: parseFloat(item.price || '0'),
        is_offered: item.is_offered ?? true,
        service_type: item.service_name.toLowerCase() === 'home' || item.service_name.toLowerCase() === 'vehicle' 
          ? item.service_name.toLowerCase() as 'home' | 'vehicle'
          : undefined,
        created_at: item.created_at,
        updated_at: item.updated_at
      })) || [];

      setServices(mappedServices);
      setError(null);
    } catch (err) {
      console.error('Error loading services:', err);
      setError(err instanceof Error ? err : new Error('Failed to load services'));
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  }, [businessId, isSignedIn]);

  const addService = async (service: Omit<Service, 'id' | 'created_at' | 'updated_at'>) => {
    if (!businessId || !isSignedIn) {
      throw new Error('Not authenticated or no business selected');
    }

    try {
      setSaving(true);
      
      const { data, error: insertError } = await supabase
        .from('services')
        .insert({
          user_id: businessId,
          service_name: service.service_name,
          price: service.price.toString(),
          is_offered: service.is_offered
        })
        .select()
        .single();

      if (insertError) throw insertError;

      const newService: Service = {
        id: data.id,
        service_name: data.service_name,
        price: parseFloat(data.price || '0'),
        is_offered: data.is_offered ?? true,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      setServices(prev => [...prev, newService]);
      toast.success('Service added successfully');
      return newService;
    } catch (err) {
      console.error('Error adding service:', err);
      const message = err instanceof Error ? err.message : 'Failed to add service';
      toast.error(message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const updateService = async (serviceId: string, updates: Partial<Service>) => {
    if (!businessId || !isSignedIn) {
      throw new Error('Not authenticated or no business selected');
    }

    try {
      setSaving(true);
      
      const updateData: any = {
        updated_at: new Date().toISOString()
      };
      
      if (updates.service_name !== undefined) updateData.service_name = updates.service_name;
      if (updates.price !== undefined) updateData.price = updates.price.toString();
      if (updates.is_offered !== undefined) updateData.is_offered = updates.is_offered;

      const { data, error: updateError } = await supabase
        .from('services')
        .update(updateData)
        .eq('id', serviceId)
        .eq('user_id', businessId)
        .select()
        .single();

      if (updateError) throw updateError;

      const updatedService: Service = {
        id: data.id,
        service_name: data.service_name,
        price: parseFloat(data.price || '0'),
        is_offered: data.is_offered ?? true,
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      setServices(prev => prev.map(s => s.id === serviceId ? updatedService : s));
      toast.success('Service updated successfully');
      return updatedService;
    } catch (err) {
      console.error('Error updating service:', err);
      const message = err instanceof Error ? err.message : 'Failed to update service';
      toast.error(message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const removeService = async (serviceId: string) => {
    if (!businessId || !isSignedIn) {
      throw new Error('Not authenticated or no business selected');
    }

    try {
      setSaving(true);
      
      const { error: deleteError } = await supabase
        .from('services')
        .delete()
        .eq('id', serviceId)
        .eq('user_id', businessId);

      if (deleteError) throw deleteError;

      setServices(prev => prev.filter(s => s.id !== serviceId));
      toast.success('Service removed successfully');
    } catch (err) {
      console.error('Error removing service:', err);
      const message = err instanceof Error ? err.message : 'Failed to remove service';
      toast.error(message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const toggleServiceOffered = async (serviceId: string) => {
    const service = services.find(s => s.id === serviceId);
    if (!service) return;

    return updateService(serviceId, { is_offered: !service.is_offered });
  };

  const updateServicePrice = async (serviceId: string, price: number) => {
    return updateService(serviceId, { price });
  };

  // Load services when business ID changes
  useEffect(() => {
    loadServices();
  }, [loadServices]);

  return {
    services,
    loading,
    error,
    saving,
    addService,
    updateService,
    removeService,
    toggleServiceOffered,
    updateServicePrice,
    reloadServices: loadServices
  };
}